import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "@tanstack/react-router";
import { PlayCircle } from "@untitledui/icons";
import { Button } from "@/components/base/buttons/button";
import { streamSse } from "@/lib/sse";
import { ApiError } from "@/lib/apiClient";
import { formatDateTime } from "@/lib/format";
import {
  OTP_TEST_MODES,
  useOtpPasswordScenarios,
  type OtpPasswordProgress,
  type OtpPasswordResult,
  type OtpTestMode,
} from "@/lib/testOtpPassword";
import { Field, ProgressSteps, Row, TestHttpLogs, inputCls } from "./shared";
import { SectionHeader } from "@/components/application/section-headers/section-headers";
import { AppSelect } from "@/components/app/AppSelect";

export function TestOtpPasswordPage() {
  const { t } = useTranslation();
  const scenarios = useOtpPasswordScenarios();

  const [scenario, setScenario] = useState("");
  const [mode, setMode] = useState<OtpTestMode>("OTP_GENERATION");
  const [tokenId, setTokenId] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [otpCode, setOtpCode] = useState("");

  const [steps, setSteps] = useState<OtpPasswordProgress[]>([]);
  const [result, setResult] = useState<OtpPasswordResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [running, setRunning] = useState(false);

  const applyScenario = (name: string) => {
    setScenario(name);
    const s = scenarios.data?.find((x) => x.name === name);
    if (!s) return;
    setMode(s.testMode);
    setTokenId(s.tokenId);
    setCurrentPassword(s.currentPassword ?? "");
    setNewPassword(s.newPassword ?? "");
    setOtpCode(s.existingOtp ?? "");
  };

  const start = async () => {
    setError(null);
    if (!tokenId) {
      setError(t("testing.otp.tokenRequired"));
      return;
    }
    const body: Record<string, unknown> = { tokenId, testMode: mode };
    if (mode === "OTP_GENERATION") {
      if (!currentPassword) return setError(t("testing.otp.currentPwRequired"));
      body.currentPassword = currentPassword;
    } else if (mode === "PASSWORD_CHANGE") {
      if (!currentPassword || !newPassword) return setError(t("testing.otp.oldNewPwRequired"));
      if (newPassword.length < 4) return setError(t("testing.otp.newPwMin"));
      body.currentPassword = currentPassword;
      body.newPassword = newPassword;
    } else {
      if (!otpCode) return setError(t("testing.otp.otpRequired"));
      body.otpCode = otpCode;
    }

    setSteps([]);
    setResult(null);
    setRunning(true);
    try {
      await streamSse("/admin/api/v1/testing/otp-password/start", body, (ev) => {
        if (ev.event !== "progress" || !ev.data) return;
        const progress = JSON.parse(ev.data) as OtpPasswordProgress;
        setSteps((s) => [...s, progress]);
        if (progress.status === "COMPLETED" && progress.data) setResult(progress.data);
        else if (progress.status === "FAILED") setError(progress.message);
      });
    } catch (e) {
      setError(e instanceof ApiError ? `HTTP ${e.status}` : (e as Error).message);
    } finally {
      setRunning(false);
    }
  };

  const modeHelp: Record<OtpTestMode, string> = {
    OTP_GENERATION: t("testing.otp.help.generation"),
    PASSWORD_CHANGE: t("testing.otp.help.passwordChange"),
    OTP_VERIFY: t("testing.otp.help.verify"),
  };

  return (
    <div className="flex flex-col gap-6">
      <SectionHeader.Root>
        <SectionHeader.Group>
          <div className="flex-1">
            <SectionHeader.Heading>{t("testing.otp.title")}</SectionHeader.Heading>
            <SectionHeader.Subheading>{t("testing.otp.subtitle")}</SectionHeader.Subheading>
          </div>
        </SectionHeader.Group>
      </SectionHeader.Root>

      <div className="flex flex-col gap-4 rounded-xl bg-primary p-5 ring-1 ring-secondary">
        <h2 className="text-md font-semibold text-primary">{t("testing.activation.params")}</h2>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label={t("testing.otp.scenario")}>
            <AppSelect value={scenario} onChange={(e) => applyScenario(e.target.value)}>
              <option value="">{t("testing.otp.scenarioNone")}</option>
              {(scenarios.data ?? []).map((s) => (
                <option key={s.name} value={s.name}>
                  {s.name}
                </option>
              ))}
            </AppSelect>
          </Field>
          <Field label={t("testing.otp.mode")} hint={modeHelp[mode]}>
            <AppSelect value={mode} onChange={(e) => setMode(e.target.value as OtpTestMode)}>
              {OTP_TEST_MODES.map((m) => (
                <option key={m} value={m}>
                  {t(`testing.otp.mode.${m}`)}
                </option>
              ))}
            </AppSelect>
          </Field>
          <Field label={`${t("testing.otp.tokenId")} *`}>
            <input className={inputCls} value={tokenId} onChange={(e) => setTokenId(e.target.value)} />
          </Field>
        </div>

        {/* Mode-specific */}
        {mode === "OTP_GENERATION" && (
          <Field label={`${t("testing.otp.currentPassword")} *`}>
            <input
              className={inputCls}
              type="password"
              autoComplete="off"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
            />
          </Field>
        )}
        {mode === "PASSWORD_CHANGE" && (
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label={`${t("testing.otp.oldPassword")} *`}>
              <input
                className={inputCls}
                type="password"
                autoComplete="off"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
              />
            </Field>
            <Field label={`${t("testing.otp.newPassword")} *`}>
              <input
                className={inputCls}
                type="password"
                autoComplete="new-password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
              />
            </Field>
          </div>
        )}
        {mode === "OTP_VERIFY" && (
          <Field label={`${t("testing.otp.otpCode")} *`}>
            <input className={inputCls} value={otpCode} onChange={(e) => setOtpCode(e.target.value)} />
          </Field>
        )}

        <div>
          <Button size="lg" iconLeading={PlayCircle} isDisabled={running} onPress={() => void start()}>
            {running ? t("testing.running") : t("testing.otp.start")}
          </Button>
        </div>
      </div>

      <ProgressSteps steps={steps} />

      {result && (
        <div className="flex flex-col gap-3 rounded-xl bg-primary p-5 ring-1 ring-secondary">
          <h2 className="text-md font-semibold text-success-primary">✓ {t("testing.otp.success")}</h2>
          <dl className="grid gap-x-6 gap-y-1 text-sm sm:grid-cols-2">
            <Row label={t("testing.otp.tokenId")} value={result.tokenId} mono />
            <Row label={t("testing.otp.mode")} value={t(`testing.otp.mode.${result.testMode}`)} />
            <Row label={t("testing.duration")} value={`${result.durationMs} ms`} />
            <Row label={t("testing.completedAt")} value={formatDateTime(result.completedAt)} />
            {result.testMode === "OTP_GENERATION" && result.generatedOtp && (
              <Row label={t("testing.otp.generatedOtp")} value={result.generatedOtp} mono />
            )}
            {result.testMode === "OTP_GENERATION" && result.generatedOtpExpiresAt && (
              <Row label={t("testing.otp.expiresAt")} value={formatDateTime(result.generatedOtpExpiresAt)} />
            )}
            {result.testMode === "PASSWORD_CHANGE" && (
              <Row label={t("testing.otp.passwordChanged")} value={result.passwordChanged ? t("common.yes") : t("common.no")} />
            )}
            {result.testMode === "OTP_VERIFY" && (
              <Row label={t("testing.otp.otpValid")} value={result.otpValid ? t("common.yes") : t("common.no")} />
            )}
            <Row label={t("testing.httpCalls")} value={String(result.httpLogs?.length ?? 0)} />
          </dl>
          <div className="flex flex-wrap gap-2">
            <Link
              to="/tokens/$tokenId"
              params={{ tokenId: result.tokenId }}
              className="rounded-lg bg-brand-solid px-3 py-2 text-sm font-semibold text-white hover:bg-brand-solid_hover"
            >
              {t("testing.viewToken")}
            </Link>
            <a
              href={`/admin/api/v1/testing/otp-password/export/${encodeURIComponent(result.tokenId)}?format=json`}
              target="_blank"
              rel="noreferrer"
              className="rounded-lg px-3 py-2 text-sm font-medium text-secondary ring-1 ring-primary hover:bg-primary_hover"
            >
              {t("testing.exportJson")}
            </a>
            <a
              href={`/admin/api/v1/testing/otp-password/export/${encodeURIComponent(result.tokenId)}?format=csv`}
              target="_blank"
              rel="noreferrer"
              className="rounded-lg px-3 py-2 text-sm font-medium text-secondary ring-1 ring-primary hover:bg-primary_hover"
            >
              {t("testing.exportCsv")}
            </a>
          </div>
        </div>
      )}

      {error && !result && (
        <div className="rounded-xl bg-primary p-5 ring-1 ring-error">
          <h2 className="text-md font-semibold text-error-primary">✗ {t("testing.otp.failed")}</h2>
          <p className="mt-1 text-sm text-secondary">{error}</p>
        </div>
      )}

      {result && <TestHttpLogs logs={result.httpLogs ?? []} />}
    </div>
  );
}
